/**
 * AppErrorBoundary — the app's single top-level React error boundary.
 *
 * The web app previously had NO error boundary anywhere, so any uncaught render
 * error (e.g. a live transport surface throwing on the deployed dev build)
 * unmounted the whole tree to a BLANK WHITE SCREEN with no recovery path — the
 * "login worked but I got a blank screen" failure. This boundary catches those
 * render errors and shows a recoverable fallback instead of a blank page, and
 * logs the underlying error so it is diagnosable rather than silent.
 *
 * It is mounted inside the Router (so the fallback can offer navigation) and is
 * keyed by the current pathname via {@link RouteErrorBoundary} so that
 * navigating to a different route clears a stuck error state without a full
 * reload.
 */

import React from 'react';
import { useLocation } from 'react-router-dom';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
  /** Changing this value resets the boundary (see RouteErrorBoundary). */
  resetKey?: string;
  /** Optional sign-out affordance surfaced on the fallback. */
  onSignOut?: () => void;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

class AppErrorBoundaryInner extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidUpdate(prevProps: AppErrorBoundaryProps): void {
    // Reset the caught error when the reset key (route path) changes, so moving
    // to a healthy route recovers without a hard reload.
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    // Surface the real error for diagnosis (the blank screen hid it entirely).
    console.error('[AppErrorBoundary] Uncaught render error:', error, info.componentStack);
  }

  render(): React.ReactNode {
    if (this.state.error) {
      return (
        <div
          data-testid="app-error-boundary"
          role="alert"
          className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center"
        >
          <h1 className="font-display text-2xl text-foreground">Something went wrong</h1>
          <p className="max-w-md text-body-sm text-muted-foreground">
            We hit an unexpected error loading this page. Try reloading — if it keeps happening,
            please let the pageant team know.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-input bg-primary px-4 py-2 text-body-sm font-medium text-primary-foreground shadow-subtle transition-colors hover:bg-primary/90"
            >
              Reload
            </button>
            {this.props.onSignOut ? (
              <button
                type="button"
                onClick={this.props.onSignOut}
                className="rounded-input border border-border px-4 py-2 text-body-sm font-medium text-foreground transition-colors hover:bg-muted"
              >
                Sign out
              </button>
            ) : null}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/**
 * Route-aware wrapper: keys the boundary by the current pathname so a caught
 * error is cleared when the user navigates to a different route.
 */
export function RouteErrorBoundary({
  children,
  onSignOut,
}: {
  children: React.ReactNode;
  onSignOut?: () => void;
}): React.ReactElement {
  const location = useLocation();
  return (
    <AppErrorBoundaryInner resetKey={location.pathname} onSignOut={onSignOut}>
      {children}
    </AppErrorBoundaryInner>
  );
}

export default RouteErrorBoundary;
